import IconButton from '@mui/material/IconButton';
import LikedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import DislikedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import React from 'react';
import useAppDispatch from '@data/useAppDispatch';
import {
  markContentAsHelpful,
  markContentAsNotHelpful,
} from '@data/contents/Contents.thunk';
import ReplyIcon from '@mui/icons-material/Reply';
import PostShareMenu from '@shared/components/post/components/post-intraction-bar/PostShareMenu';
import MakePostDialogProvider from '@shared/components/make-post/MakePostDialogProvider';
import { ShareInChatDialog } from '@shared/components/shared-post/ShareInChatDialog';

const ContentFeedback: React.FC<{
  contentId: string;
  contentMark?: 'helpful' | 'unhelpful';
}> = ({ contentId, contentMark }) => {
  const dispatch = useAppDispatch();

  const [shareAnchorEl, setShareAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const handleClose = React.useCallback(
    (setAnchorEl) => () => {
      setAnchorEl(null);
    },
    [],
  );
  const handleOpen = React.useCallback(
    (setAnchorEl, anchorEl) => (event: React.MouseEvent<HTMLElement>) => {
      if (anchorEl) handleClose(setAnchorEl)();
      else setAnchorEl(event.currentTarget);
    },
    [handleClose],
  );

  const [openDialog, setDialogOpen] = React.useState(false);
  const openResharePostDialog = React.useCallback(() => {
    setDialogOpen(true);
  }, []);

  const [openShareInChatDialog, setOpenShareInChatDialog] =
    React.useState(false);

  const handleShareOnChat = React.useCallback(() => {
    setOpenShareInChatDialog(true);
    handleClose(setShareAnchorEl);
  }, [handleClose, setShareAnchorEl]);

  const buttonList = React.useMemo(
    () => [
      {
        Icon: <LikedIcon />,
        title: 'Helpful',
        action: () => {
          dispatch(markContentAsHelpful(contentId));
        },
        marked: contentMark === 'helpful',
      },
      {
        Icon: <DislikedIcon />,
        title: 'Not Helpful',
        action: () => {
          dispatch(markContentAsNotHelpful(contentId));
        },
        marked: contentMark === 'unhelpful',
      },
      {
        Icon: (
          <IconButton
            onClick={handleOpen(setShareAnchorEl, shareAnchorEl)}
            className="-mb-2"
          >
            <ReplyIcon
              className="!fill-on-secondary"
              style={{
                transform: 'scale(-1, 1)',
              }}
            />
            <PostShareMenu
              anchorEl={shareAnchorEl}
              handleClose={handleClose(setShareAnchorEl)}
              openResharePostDialog={openResharePostDialog}
              handleShareOnChat={handleShareOnChat}
            />
          </IconButton>
        ),
        title: 'Share',
        action: () => {
          handleOpen(setShareAnchorEl, shareAnchorEl);
        },
        marked: false,
      },
    ],
    [
      contentId,
      contentMark,
      dispatch,
      handleOpen,
      handleClose,
      shareAnchorEl,
      openResharePostDialog,
      setShareAnchorEl,
      handleShareOnChat,
    ],
  );

  const renderFeedbackButton = React.useCallback(
    ({ Icon, title, action, marked }) => {
      return (
        <IconButton
          key={`feedback-${contentId}-${title}`}
          className={`flex items-center justify-center flex-1 h-full rounded-none ${
            marked ? 'text-brand-primary' : 'text-[#4D565F]'
          }`}
          onClick={!marked && action}
        >
          <span className="w-5 h-full  flex-col-center">{Icon}</span>
          <h6 className="h-full ml-2 h6 flex-col-center">{title}</h6>
        </IconButton>
      );
    },
    [contentId],
  );

  return (
    <div
      className="sticky bottom-0 flex items-center justify-center w-full h-12 bg-white"
      style={{
        position: 'sticky',
        bottom: 0,
      }}
    >
      <ShareInChatDialog
        open={openShareInChatDialog}
        onClose={() => setOpenShareInChatDialog(false)}
        id={contentId}
        type="discover"
      />
      <MakePostDialogProvider
        open={{
          mode: openDialog ? 'reshare' : 'close',
          contentId,
        }}
        closeDialog={() => {
          setDialogOpen(false);
        }}
      />
      {buttonList.map((button, _) => {
        return renderFeedbackButton(button);
      })}
    </div>
  );
};

export default ContentFeedback;
